import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { StateList } from '@core/constants/states';
import { amountOfCharactersValidator, customMaskValidator, phoneNumberValidator } from '@shared/validators';
import { noWhiteSpaceValidator } from '@mynexus/mynexus-ui-lib';
import { ZIP_CODE_EXAMPLE, ZIP_CODE_REGEX } from '@shared/constants';
import { AuthorizationFacilityContactPAC, ServicingFacilityContactDetailsPAC } from '@shared/model';
import { isEmpty, omit } from 'lodash-es';
import { SubSink } from 'subsink';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'servicing-facility-new',
    templateUrl: './servicing-facility-new.component.html',
    styleUrls: ['./servicing-facility-new.component.scss'],
    standalone: false
})
export class ServicingFacilityNewComponent implements OnInit, OnDestroy {
  @Input() contactDetails: ServicingFacilityContactDetailsPAC;
  @Input() isExternalFlow!: boolean;
  @Input() newServicingFacility: AuthorizationFacilityContactPAC;
  @Output() newServicingFacilityChange = new EventEmitter<AuthorizationFacilityContactPAC>();

  form: FormGroup;

  readonly states = StateList;

  private subs = new SubSink();

  ngOnInit() {
    this.createServicingFacilityForm();

    if (this.newServicingFacility) {
      this.form.patchValue(this.newServicingFacility);

      this.emitUpdate();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  setContactDetails(details: ServicingFacilityContactDetailsPAC): void {
    this.contactDetails = details;

    this.emitUpdate();
  }

  private createServicingFacilityForm() {
    this.form = new FormGroup({
      facilityNotFound: new FormControl(!!this.newServicingFacility, Validators.requiredTrue),
      npi: new FormControl(null, [Validators.required, amountOfCharactersValidator(10)]),
      facilityName: new FormControl(null, [Validators.required, noWhiteSpaceValidator, Validators.maxLength(100)]),
      taxId: new FormControl(null, [Validators.required, amountOfCharactersValidator(9)]),
      phone: new FormControl(null, phoneNumberValidator),
      fax: new FormControl(null, phoneNumberValidator),
      address: new FormControl(null, [noWhiteSpaceValidator, Validators.maxLength(100)]),
      city: new FormControl(null, [noWhiteSpaceValidator, Validators.minLength(2), Validators.maxLength(50)]),
      state: new FormControl(null, Validators.maxLength(2)),
      zipCode: new FormControl(null, customMaskValidator(ZIP_CODE_REGEX, ZIP_CODE_EXAMPLE)),
    });

    this.subs.sink = this.form.valueChanges.subscribe(() => {
      this.emitUpdate();
    });
  }

  private emitUpdate(): void {
    const valid = this.form.valid && !isEmpty(this.contactDetails);
    const value = {
      ...omit(this.form.value, 'facilityNotFound') as AuthorizationFacilityContactPAC,
      ...this.contactDetails
    };

    this.newServicingFacilityChange.emit(valid ? value : null);
  }
}
